import { Link, NavLink } from "react-router-dom";
import {
  LayoutDashboard, Users, UserCircle2, Briefcase,
  CheckSquare, Bell, Wallet, Settings, LogOut,
  Sun, Moon, ListChecks, Calendar, FolderOpen, Shield,
  BarChart3, FileText, Sparkles, Megaphone, Building2, Repeat, Film,
  Palette, Edit3, Video, Camera, Trash2, MessageSquare, TrendingUp, Send, Target,
  HardDrive, Layers, Wand2, FileSpreadsheet, Link2, Trophy, ClipboardList, Eye, BookOpen, Clock, Database, Heart, ClipboardCheck,
  Menu,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import useDesktopNotifications from "@/hooks/useDesktopNotifications";
import RenewalDropdown from "@/components/layout/RenewalDropdown";
import TaskReminder from "@/components/layout/TaskReminder";
import InstallAppPrompt from "@/components/InstallAppPrompt";

const SALES_ROLES = ["admin", "manager", "sales", "account_manager"];
const FINANCE_ROLES = ["admin", "manager", "accountant", "finance"];
const ADMIN_ONLY = ["admin", "manager"];
const ADMIN_MANAGER = ["super_admin", "admin", "manager"];
const HR_ROLES = ["super_admin", "admin", "manager"];

// Each item can declare a `perm` (permission key from the catalogue) to gate
// its visibility on the user's effective_permissions. Items without a `perm`
// remain visible to anyone whose `roles` whitelist matches (or to everyone
// when no whitelist is set). Admins always see everything.
export const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  // Owner-only supervision panel — single-page snapshot of the agency.
  { to: "/supervision", label: "Supervision", icon: Eye, roles: ["super_admin", "admin"] },
  { to: "/management-calendar", label: "Mgmt Calendar", icon: Calendar, roles: ["super_admin", "admin", "manager", "account_manager", "video_editor", "graphic_designer", "art_director", "content_creator", "paid_ads", "photographer"] },
  { to: "/operation-calendar", label: "Op Calendar", icon: Calendar, roles: ["super_admin", "admin", "manager", "account_manager"] },
  { to: "/work-tracker", label: "Work Tracker", icon: Clock },
  { to: "/retention", label: "Retention", icon: Heart, roles: ["super_admin", "admin", "manager", "account_manager", "retention_supervisor"] },
  { to: "/loyalty", label: "Loyalty & Growth", icon: Trophy, roles: ["super_admin", "admin", "manager", "account_manager", "retention_supervisor"] },
  { to: "/dept-reports", label: "Dept Reports", icon: ClipboardCheck, roles: ["super_admin", "admin", "manager", "account_manager", "retention_supervisor", "sales_supervisor", "content_lead", "design_lead", "video_lead", "head_of_content", "head_of_design", "head_of_video", "head_of_ops"] },
  { to: "/leads", label: "Leads / CRM", icon: UserCircle2, roles: SALES_ROLES, perm: "view_leads" },
  { to: "/clients", label: "Clients", icon: Users, perm: "view_clients" },
  // Restored Feb 2026, relabelled "Requests" — central cross-department
  // request board. The /tasks URL stays as an alias so existing
  // notification deep-links still work.
  { to: "/requests", label: "Requests", icon: ListChecks, perm: "view_tasks" },
  { to: "/cases", label: "Cases", icon: FolderOpen, perm: "view_cases" },
  { to: "/schedule", label: "Schedule", icon: Calendar, perm: "view_schedules" },
  { to: "/automated-calendar", label: "Automated Calendar", icon: Sparkles,
    roles: [...ADMIN_MANAGER, "account_manager", "content_creator", "art_director"], perm: "view_schedules" },
  // Department schedules — only members of that department + admins/managers/AMs
  { to: "/video-editing", label: "Video Editing", icon: Film,
    roles: [...ADMIN_MANAGER, "account_manager", "video_editor", "art_director"], perm: "view_schedules" },
  { to: "/graphic-design", label: "Graphic Design", icon: Palette,
    roles: [...ADMIN_MANAGER, "account_manager", "art_director", "graphic_designer", "paid_ads"], perm: "view_schedules" },
  { to: "/content-creator", label: "Content Creator", icon: Edit3,
    roles: [...ADMIN_MANAGER, "account_manager", "paid_ads", "content_creator"], perm: "view_schedules" },
  { to: "/paid-ads-schedule", label: "Paid Ads Schedule", icon: Megaphone,
    roles: [...ADMIN_MANAGER, "account_manager", "paid_ads"], perm: "view_schedules" },
  { to: "/shooting", label: "Shooting", icon: Camera },
  { to: "/account-management", label: "Account Managers", icon: UserCircle2,
    roles: [...ADMIN_MANAGER, "account_manager"] },
  { to: "/calendar", label: "Calendar", icon: Calendar },
  { to: "/meetings", label: "Meetings", icon: Video },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: [...ADMIN_ONLY, "accountant", "finance", "account_manager"], perm: "view_reports" },
  { to: "/approvals", label: "Approvals", icon: ListChecks, perm: "approve_files" },
  { to: "/manager-todos", label: "Manager To-Do", icon: ClipboardList, roles: ADMIN_MANAGER },
  { to: "/services", label: "Services & Packages", icon: Briefcase, roles: ADMIN_ONLY, perm: "view_services" },
  { to: "/quotations", label: "Quotations", icon: FileText, roles: SALES_ROLES, perm: "view_quotations" },
  { to: "/paid-ads", label: "Paid Ads", icon: Megaphone, roles: [...ADMIN_ONLY, "paid_ads", "account_manager"] },
  { to: "/chat", label: "Internal Chat", icon: MessageSquare },
  { to: "/trends", label: "Trend Hunter", icon: TrendingUp, roles: [...ADMIN_ONLY, "account_manager", "art_director", "paid_ads", "content_creator", "graphic_designer", "video_editor"] },
  { to: "/finance", label: "Finance", icon: Wallet, roles: FINANCE_ROLES, perm: "view_payments" },
  { to: "/monthly-report", label: "Monthly Report", icon: FileSpreadsheet,
    roles: [...FINANCE_ROLES, "account_manager"] },
  { to: "/ai-tools", label: "AI Tools", icon: Sparkles, roles: [...ADMIN_MANAGER, "account_manager", "art_director", "video_editor", "graphic_designer", "content_creator", "paid_ads"] },
  { to: "/ai-website-builder", label: "AI Website Builder", icon: Wand2, roles: [...ADMIN_MANAGER, "account_manager"] },
  { to: "/transfers", label: "Transfers", icon: Send },
  { to: "/drive", label: "Drive", icon: HardDrive },
  { to: "/pdf-to-link", label: "PDF to Link", icon: Link2, roles: [...ADMIN_MANAGER, "account_manager"] },
  { to: "/bulk-post", label: "Bulk Post", icon: Layers, roles: [...ADMIN_MANAGER, "account_manager"], perm: "manage_schedules" },
  { to: "/sales-hunter", label: "Sales Hunter", icon: Target, roles: SALES_ROLES },
  { to: "/bulk-whatsapp", label: "Bulk WhatsApp", icon: Megaphone, roles: ["admin", "super_admin", "manager"] },
  { to: "/employees", label: "Team / Employees", icon: Users, roles: [...ADMIN_ONLY, "account_manager"], perm: "view_employees" },
  { to: "/performance", label: "Performance", icon: Trophy },
  { to: "/hr", label: "HR", icon: Building2, roles: HR_ROLES, perm: "view_hr" },
  { to: "/task-templates", label: "Automations", icon: Repeat, roles: HR_ROLES, perm: "run_automations" },
  { to: "/roles", label: "Roles & Permissions", icon: Shield, roles: ["admin", "super_admin"], perm: "manage_roles" },
  { to: "/trash", label: "Trash", icon: Trash2, roles: ["admin", "super_admin", "manager"] },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/permissions", label: "Permissions", icon: Shield, roles: ["super_admin", "admin"], perm: "manage_roles" },
  { to: "/admin/dashboard", label: "Admin Dashboard", icon: Database, roles: ["super_admin", "admin"] },
  { to: "/customize", label: "Customize", icon: Palette, roles: ["super_admin", "admin"] },
  { to: "/manager/daily-tasks", label: "Daily Tasks (Team)", icon: ClipboardList, roles: ["super_admin", "admin", "manager"] },
  { to: "/designer-grid", label: "Designer Grid", icon: Palette, roles: ["super_admin", "admin", "manager", "account_manager", "art_director"] },
  { to: "/audit-log", label: "Audit Log", icon: BookOpen, roles: ["super_admin", "admin", "manager"] },
];

/**
 * Iter143 — mobile shell.
 *
 * The old Layout hid the sidebar with `hidden lg:flex` and exposed only 4
 * items in a bottom nav, leaving ~30 pages unreachable on phones. Now:
 *   • useNavVisibility() computes the same visible-items list for both
 *     the desktop sidebar and the mobile drawer.
 *   • On < lg, we render a hamburger button in the TopBar that opens a
 *     Sheet drawer holding the entire nav (identical items, badges,
 *     order, active state).
 *   • BottomNav retained for one-thumb quick jumps.
 */
function useNavVisibility(user) {
  const role = user?.role;
  const [counts, setCounts] = useState({});
  const [navConfig, setNavConfig] = useState({ order: [], hidden: [] });

  useEffect(() => {
    let timer = null;
    let mounted = true;
    const fetchCounts = async () => {
      try {
        const { data } = await api.get("/me/badge-counts");
        if (mounted) setCounts(data || {});
      } catch (err) {
        if (err?.response?.status !== 401) logger.error("badge-counts failed:", err);
      }
    };
    fetchCounts();
    timer = setInterval(fetchCounts, 60_000);
    const onFocus = () => fetchCounts();
    window.addEventListener("focus", onFocus);
    return () => { mounted = false; if (timer) clearInterval(timer); window.removeEventListener("focus", onFocus); };
  }, []);

  useEffect(() => {
    let mounted = true;
    api.get("/nav-config")
      .then(({ data }) => { if (mounted) setNavConfig(data || { order: [], hidden: [] }); })
      .catch(() => { /* silent */ });
    const onUpd = (e) => setNavConfig(e.detail || { order: [], hidden: [] });
    window.addEventListener("nav-config-updated", onUpd);
    return () => { mounted = false; window.removeEventListener("nav-config-updated", onUpd); };
  }, []);

  const visible = useMemo(() => {
    const userPerms = new Set(user?.effective_permissions || user?.permissions || []);
    const hiddenPages = new Set(user?.hidden_pages || []);
    const globalHidden = new Set(navConfig.hidden || []);
    const allRoles = new Set([role, ...(user?.extra_roles || [])]);
    let list = navItems.filter((i) => {
      if (hiddenPages.has(i.to) || globalHidden.has(i.to)) return false;
      if (allRoles.has("admin") || allRoles.has("super_admin")) return true;
      if (i.roles && !i.roles.some((r) => allRoles.has(r))) return false;
      if (i.perm && !userPerms.has(i.perm)) return false;
      return true;
    });
    if (navConfig.order?.length) {
      const idx = new Map(navConfig.order.map((to, i) => [to, i]));
      list = [...list].sort((a, b) => {
        const ai = idx.has(a.to) ? idx.get(a.to) : 9999;
        const bi = idx.has(b.to) ? idx.get(b.to) : 9999;
        return ai - bi;
      });
    }
    return list;
  }, [role, user, navConfig]);

  return { visible, counts };
}

function NavList({ items, counts, onNavigate, testidPrefix = "nav" }) {
  return (
    <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
      {items.map(item => {
        const n = counts[item.to] || 0;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            onClick={onNavigate}
            data-testid={`${testidPrefix}-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                isActive
                  ? "bg-foreground text-background border-l-2 border-teal"
                  : "text-foreground/80 hover:bg-teal/5 hover:text-foreground hover:border-l-2 hover:border-teal/40"
              }`
            }
          >
            <item.icon size={18} strokeWidth={1.5} />
            <span className="flex-1 truncate">{item.label}</span>
            {n > 0 && (
              <span
                className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-[#EF4444] text-white text-[10px] font-semibold flex items-center justify-center"
                data-testid={`${testidPrefix}-badge-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                aria-label={`${n} pending item${n === 1 ? "" : "s"}`}
              >
                {n > 99 ? "99+" : n}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function Sidebar({ user }) {
  const { visible, counts } = useNavVisibility(user);
  return (
    <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-card sticky top-0 h-screen" data-testid="sidebar">
      <div className="px-6 py-6 border-b border-border">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-teal text-white flex items-center justify-center font-bold heading">S</div>
          <span className="heading text-xl font-semibold tracking-tight">SenrizeFlow</span>
        </Link>
      </div>
      <NavList items={visible} counts={counts} />
    </aside>
  );
}

function MobileNavDrawer({ user, open, onOpenChange }) {
  const { visible, counts } = useNavVisibility(user);
  const location = useLocation();
  // Close the drawer when the user navigates to a new route from inside it.
  // We ONLY react to pathname changes — never to `open` itself — otherwise
  // the effect would immediately close the drawer as soon as we open it.
  const prevPath = useRef(location.pathname);
  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      prevPath.current = location.pathname;
      onOpenChange(false);
    }
  }, [location.pathname, onOpenChange]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="p-0 w-72 max-w-[85vw] flex flex-col" data-testid="mobile-nav-drawer">
        <SheetHeader className="px-6 py-5 border-b border-border">
          <SheetTitle className="flex items-center gap-2 text-left">
            <div className="w-8 h-8 rounded-md bg-teal text-white flex items-center justify-center font-bold heading">S</div>
            <span className="heading text-xl font-semibold tracking-tight">SenrizeFlow</span>
          </SheetTitle>
        </SheetHeader>
        <NavList
          items={visible}
          counts={counts}
          onNavigate={() => onOpenChange(false)}
          testidPrefix="mobile-nav"
        />
      </SheetContent>
    </Sheet>
  );
}

const mobileNav = [
  { to: "/", label: "Home", icon: LayoutDashboard },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/approvals", label: "Approve", icon: ListChecks },
  { to: "/settings", label: "Profile", icon: Settings },
];

function BottomNav() {
  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-50 glass border-t border-border pb-[env(safe-area-inset-bottom)]"
      data-testid="bottom-nav"
    >
      <div className="grid grid-cols-5">
        {mobileNav.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            data-testid={`bottom-nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 py-3 text-[11px] ${
                isActive ? "text-foreground" : "text-muted-foreground"
              }`
            }
          >
            <item.icon size={20} strokeWidth={1.5} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

function TopBar({ unread, onOpenMobileNav }) {
  const { user, logout } = useAuth();
  const { resolvedTheme, toggle } = useTheme();
  const [quote, setQuote] = useState("");

  useEffect(() => {
    let mounted = true;
    const fetchQuote = async () => {
      try {
        const { data } = await api.get("/daily-quote");
        if (mounted && data?.quote) setQuote(data.quote);
      } catch (err) {
        if (err?.response?.status !== 401) {
          logger.error("TopBar.fetchQuote failed:", err);
        }
      }
    };
    fetchQuote();
    return () => { mounted = false; };
  }, []);

  return (
    <header className="sticky top-0 z-40 glass border-b border-border" data-testid="topbar">
      <div className="flex items-center justify-between px-3 sm:px-4 lg:px-8 h-14 gap-2">
        <div className="flex items-center gap-2 lg:hidden min-w-0">
          {/* Iter143 — hamburger opens the full sidebar as a drawer.
              Without this, ~30 pages were unreachable on phones. */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onOpenMobileNav}
            aria-label="Open menu"
            data-testid="mobile-nav-toggle"
          >
            <Menu size={20} strokeWidth={1.5} />
          </Button>
          <div className="w-7 h-7 rounded-md bg-foreground text-background flex items-center justify-center font-bold heading shrink-0">S</div>
          <span className="heading text-base sm:text-lg font-semibold truncate">SenrizeFlow</span>
        </div>
        <div className="hidden lg:flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground">
          <span>SenrizeFlow</span>
          <span className="text-border">/</span>
          <span className="text-foreground">{user?.role}</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme">
            {resolvedTheme === "dark" || resolvedTheme === "midnight" || resolvedTheme === "ocean"
              ? <Sun size={18} strokeWidth={1.5} />
              : <Moon size={18} strokeWidth={1.5} />}
          </Button>
          <RenewalDropdown />
          <TaskReminder />
          <Link to="/notifications" className="relative inline-flex" data-testid="notifications-bell">
            <Button variant="ghost" size="icon" aria-label="Notifications" className={unread > 0 ? "text-teal hover:text-teal" : ""}>
              <Bell size={18} strokeWidth={1.5} />
            </Button>
            {unread > 0 && (
              <span className="absolute top-1 right-1 min-w-4 h-4 px-1 rounded-full bg-[#EF4444] text-white text-[10px] flex items-center justify-center" data-testid="notif-count">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </Link>
          <div className="hidden sm:flex items-center gap-3 pl-2 max-w-[420px]">
            <Avatar className="w-8 h-8 ring-2 ring-teal/20">
              {user?.avatar_url && <AvatarImage src={user.avatar_url} alt={user?.name || ""} />}
              <AvatarFallback>{(user?.name || "U").slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="text-xs leading-tight min-w-0">
              <div className="font-medium truncate" data-testid="user-name">{user?.name}</div>
              {quote ? (
                <div
                  className="text-[11px] italic text-muted-foreground truncate"
                  title={quote}
                  data-testid="user-daily-quote"
                >
                  <span className="text-teal mr-1">"</span>{quote}<span className="text-teal ml-1">"</span>
                </div>
              ) : (
                <div className="text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</div>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn" aria-label="Logout">
            <LogOut size={18} strokeWidth={1.5} />
          </Button>
        </div>
      </div>
    </header>
  );
}

export default function Layout({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useDesktopNotifications({ enabled: !!user, onUpdate: setUnread });
  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <Sidebar user={user} />
      <MobileNavDrawer user={user} open={mobileNavOpen} onOpenChange={setMobileNavOpen} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar unread={unread} onOpenMobileNav={() => setMobileNavOpen(true)} />
        <main className="flex-1 px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8 pb-24 lg:pb-12 max-w-[1600px] w-full mx-auto" data-testid="main-content">
          {children}
        </main>
      </div>
      <BottomNav />
      <InstallAppPrompt />
    </div>
  );
}
